import { type UIEvent } from 'react';
import { useTheme } from '../context/ThemeContext';
import type { CoinTransaction } from '../api/client';

/** Human labels for each coin-transaction type. */
export const TYPE_LABEL: Record<string, string> = {
  earn: 'Earned from order',
  redeem: 'Redeemed at checkout',
  referral_referee: 'Referral welcome bonus',
  referral_referrer: 'Referral reward',
  expire: 'Coins expired',
  reversal: 'Reversed',
  adjust: 'Adjustment',
};

/**
 * Presentational coin-activity ("Statement") list. The parent owns the
 * `useInfiniteList(rewards.coins…)` hook and passes its `items`, `loading` and
 * `onScroll` here, so there's no duplicate fetching. Shared by the Rewards page
 * and the My ZS Coins page.
 */
export default function CoinStatement({
  items,
  loading,
  onScroll,
  maxHeight = 'max-h-72',
  emptyText = 'No coin activity yet — your first delivered order will earn coins.',
}: {
  items: CoinTransaction[];
  loading: boolean;
  onScroll: (e: UIEvent<HTMLElement>) => void;
  maxHeight?: string;
  emptyText?: string;
}) {
  const { isDark } = useTheme();
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';

  return (
    <div
      onScroll={onScroll}
      className={`rounded-xl border divide-y ${isDark ? 'border-gray-800 divide-gray-800' : 'border-gray-200 divide-gray-100'} ${maxHeight} overflow-y-auto`}
    >
      {items.length ? (
        items.map((h) => (
          <div key={h.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
            <div>
              <p className="font-medium">
                {h.note || TYPE_LABEL[h.type] || h.type}
                {h.status === 'pending' ? ' (pending)' : ''}
              </p>
              <p className={`text-xs ${sub}`}>
                {new Date(h.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
            <span className={`font-bold ${h.amount >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {h.amount >= 0 ? '+' : ''}
              {h.amount}
            </span>
          </div>
        ))
      ) : (
        <p className={`px-4 py-6 text-center text-sm ${sub}`}>{emptyText}</p>
      )}
      {loading && items.length > 0 && <p className={`px-4 py-3 text-center text-xs ${sub}`}>Loading…</p>}
    </div>
  );
}
